'use client';

import React, { FC } from 'react';
import Image from 'next/image';

import { TypeSideBySideListFields } from '@/core/generated-types';
import { UniversalCta } from '@/components';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { isMuxVideo } from '@/components/common/LandingPageHero/utils';

import VideoSideBySide from './VideoSideBySide';
import MuxVideoSideBySide from './VideoMuxSideBySide';
import ImageSideBySide from './ImageSideBySide';
import classNames from 'classnames';

const DEFAULT_LIST_ICON =
  'https://images.ctfassets.net/0sea1vycfyqy/6uxFIDIxdK75AZEZ5CjYj4/520ab3acee3d0f3c441f990bd33fece4/CheckMark_Brushstroke.png';

export const SideBySideList: FC<TypeSideBySideListFields> = ({
  cta,
  title,
  listItems,
  listSubItems = [],
  mediaDesktop,
  mediaMobile,
  trailingText,
  listIcon,
  mediaRight,
  backgroundColor,
}) => {
  const isDesktop = useIsDesktop();
  const media = isDesktop ? mediaDesktop : mediaMobile;
  const isMuxMedia = media && isMuxVideo(media);
  const isMediaVideo = media && media?.fields && 'video' in media?.fields;
  const iconUrl = listIcon?.fields?.Image?.fields?.file?.url;

  return (
    <div
      className={classNames(
        'lg:flex',
        backgroundColor ? `bg-${backgroundColor?.fields?.color}` : 'bg-white',
        {
          'flex-row-reverse': mediaRight,
        },
      )}
    >
      {isMuxMedia ? (
        <MuxVideoSideBySide
          mediaDesktop={mediaDesktop}
          mediaMobile={mediaMobile}
        />
      ) : isMediaVideo ? (
        <VideoSideBySide
          mediaDesktop={mediaDesktop}
          mediaMobile={mediaMobile}
        />
      ) : (
        <ImageSideBySide
          mediaDesktop={mediaDesktop}
          mediaMobile={mediaMobile}
        />
      )}
      <div className="lg:px-6 lg:pl-20 mt-4 lg:mt-0 flex flex-col justify-center lg:max-w-[610px] px-6 lg:pr-6 lg:w-1/2 py-8 lg:py-16">
        <div className="flex mb-8">
          <p
            dangerouslySetInnerHTML={{ __html: title ?? '' }}
            className="font-[700] text-[32px] lg:text-[42px] md:w-[29rem] lg:space-y-12 leading-[45px] mt-2 lg:mt-0 "
          />
        </div>
        <div className="lg:w-[31rem] w-full lg:pr-8 mb-8">
          <ul className="lg:space-y-8 space-y-6">
            {listItems?.map((item, index) => {
              const subItem = listSubItems?.[index];
              return (
                <li className="flex items-top" key={item}>
                  <Image
                    width={32}
                    height={32}
                    alt="check-mark"
                    className="w-8 h-8 mr-2 lg:mr-4 lg:w-[40px] lg:h-[40px]"
                    src={iconUrl ? `https:${iconUrl}` : DEFAULT_LIST_ICON}
                  />
                  <div>
                    <p className="text-xl font-[600]">{item}</p>
                    {subItem && <div className="text-base">{subItem}</div>}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="flex flex-row items-center w-full mb-8 lg:mb-0 mt-0 lg:max-w-[261px] border-0">
          {cta && <UniversalCta link={cta} defaultButtonStyle="Primary" />}
        </div>
        {trailingText && (
          <div className="mt-4 text-base max-w-[350px]">{trailingText}</div>
        )}
      </div>
    </div>
  );
};

export default SideBySideList;
